import React, { useState, useEffect } from "react";
import Page from "../components/Page";
import {
  IconKey,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconClock,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconArrowLeft,
  IconLock,
  IconBed,
  IconUser,
  IconCalendar,
  IconTrash,
  IconLayersSubtract,
  IconUsb,
  IconWifi,
  IconBluetooth
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  useRfidCards,
  issueRfidCard,
  extendRfidCard,
  revokeRfidCard,
  replaceLostRfidCard,
  useRfidLocks
} from "../controllers/rfid.controller";
import ApiClient from "../helpers/ApiClient";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

export default function RfidCardIssuancePage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(1);

  const { data: cardsData, error, mutate, isLoading } = useRfidCards({
    status: statusFilter === "all" ? "" : statusFilter,
    search: searchTerm,
    page,
    per_page: 15
  });

  const { data: locks } = useRfidLocks();
  const [rooms, setRooms] = useState([]);

  // Issue Card Modal State
  const [issueRoomId, setIssueRoomId] = useState("");
  const [issueGuestName, setIssueGuestName] = useState("");
  const [issueGuestPhone, setIssueGuestPhone] = useState("");
  const [issueCardNumber, setIssueCardNumber] = useState("");
  const [issueMethod, setIssueMethod] = useState("encoder");
  const [issueValidFrom, setIssueValidFrom] = useState(() => new Date().toISOString().slice(0, 16));
  const [issueValidUntil, setIssueValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(12, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [issueSubmitting, setIssueSubmitting] = useState(false);

  // Extend Stay Modal State
  const [extendCard, setExtendCard] = useState(null);
  const [extendValidUntil, setExtendValidUntil] = useState("");
  const [extendSubmitting, setExtendSubmitting] = useState(false);

  // Replace Lost Card Modal State
  const [lostCard, setLostCard] = useState(null);
  const [replacementCardNumber, setReplacementCardNumber] = useState("");
  const [replaceSubmitting, setReplaceSubmitting] = useState(false);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const res = await ApiClient.get("/rooms");
      setRooms(res.data?.data || []);
    } catch (err) {
      console.warn("Failed to fetch rooms:", err);
    }
  };

  const openIssueModal = (prefillRoomId = "") => {
    setIssueRoomId(prefillRoomId || (rooms[0]?.id ? String(rooms[0].id) : ""));
    setIssueGuestName("");
    setIssueGuestPhone("");
    setIssueCardNumber("");
    setIssueValidFrom(new Date().toISOString().slice(0, 16));
    const nextDay = new Date();
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(12, 0, 0, 0);
    setIssueValidUntil(nextDay.toISOString().slice(0, 16));
    document.getElementById("modal-issue-card").showModal();
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    if (!issueRoomId) {
      toast.error("Please select a room");
      return;
    }
    if (!issueCardNumber.trim()) {
      toast.error("Please enter or scan a Card Number / UID");
      return;
    }

    setIssueSubmitting(true);
    try {
      const res = await issueRfidCard({
        room_id: parseInt(issueRoomId),
        guest_name: issueGuestName.trim(),
        guest_phone: issueGuestPhone.trim(),
        card_number: issueCardNumber.trim(),
        issuing_method: issueMethod,
        valid_from: issueValidFrom,
        valid_until: issueValidUntil
      });
      toast.success(res.data?.message || "Card issued successfully!");
      document.getElementById("modal-issue-card").close();
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to issue card");
    } finally {
      setIssueSubmitting(false);
    }
  };

  const openExtendModal = (card) => {
    setExtendCard(card);
    const curr = card.valid_until ? new Date(card.valid_until) : new Date();
    curr.setDate(curr.getDate() + 1);
    curr.setHours(12, 0, 0, 0);
    setExtendValidUntil(curr.toISOString().slice(0, 16));
    document.getElementById("modal-extend-card").showModal();
  };

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    setExtendSubmitting(true);
    try {
      const res = await extendRfidCard({
        card_id: extendCard.id,
        new_valid_until: extendValidUntil
      });
      toast.success(res.data?.message || "Validity extended successfully!");
      document.getElementById("modal-extend-card").close();
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to extend validity");
    } finally {
      setExtendSubmitting(false);
    }
  };

  const openLostModal = (card) => {
    setLostCard(card);
    setReplacementCardNumber("");
    document.getElementById("modal-lost-card").showModal();
  };

  const handleLostSubmit = async (e) => {
    e.preventDefault();
    if (!replacementCardNumber.trim()) {
      toast.error("Please provide the new card number");
      return;
    }
    setReplaceSubmitting(true);
    try {
      const res = await replaceLostRfidCard({
        lost_card_id: lostCard.id,
        new_card_number: replacementCardNumber.trim()
      });
      toast.success(res.data?.message || "Replacement card issued!");
      document.getElementById("modal-lost-card").close();
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to replace card");
    } finally {
      setReplaceSubmitting(false);
    }
  };

  const handleRevoke = async (card) => {
    if (!window.confirm(`Revoke RFID Card #${card.card_number} for Guest ${card.guest_name || "Unknown"}?`)) {
      return;
    }
    try {
      const res = await revokeRfidCard(card.id, "Reception Revocation");
      toast.success(res.data?.message || "Card revoked!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to revoke card");
    }
  };

  const cards = cardsData?.cards || [];
  const total = cardsData?.total || 0;
  const totalPages = cardsData?.totalPages || 1;

  // Selected room lock info
  const selectedRoomLock = (locks || []).find(l => String(l.room_id) === String(issueRoomId));

  return (
    <Page className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/rfid-doorlock"
            className="p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
          >
            <IconArrowLeft size={20} />
          </Link>
          <div className="p-2.5 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-2xl">
            <IconKey stroke={iconStroke} size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              RFID Keycard Issuance & Management
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Issue physical MIFARE/IC cards, extend stays, and revoke guest credentials
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => openIssueModal()}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 shadow-sm transition"
          >
            <IconPlus size={16} />
            <span>Issue New Card</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-neutral-800 p-1 rounded-xl">
          {[
            { id: "active", label: "Active Keys" },
            { id: "all", label: "All Cards" },
            { id: "expired", label: "Expired" },
            { id: "cancelled", label: "Revoked" },
            { id: "lost", label: "Reported Lost" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setStatusFilter(tab.id); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                statusFilter === tab.id
                  ? "bg-white dark:bg-neutral-700 text-gray-900 dark:text-white shadow-xs"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-64">
          <IconSearch size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by card #, guest, room..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Cards Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
        {cards.length === 0 ? (
          <div className="p-12 text-center">
            <IconKey size={40} className="mx-auto text-gray-400 mb-2" />
            <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
              No keycards found
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Click "Issue New Card" to assign a keycard to a guest.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4">Card # / UID</th>
                  <th className="py-3 px-4">Room & Lock</th>
                  <th className="py-3 px-4">Guest</th>
                  <th className="py-3 px-4">Method</th>
                  <th className="py-3 px-4">Validity Period</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                {cards.map((card) => {
                  const isExpired = card.valid_until && new Date(card.valid_until) < new Date();
                  return (
                    <tr key={card.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition">
                      <td className="py-3 px-4">
                        <div className="font-mono font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                          <IconKey size={14} className="text-emerald-600" />
                          <span>{card.card_number}</span>
                        </div>
                        {card.card_name && (
                          <div className="text-[11px] text-gray-400">{card.card_name}</div>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <div className="font-bold text-gray-900 dark:text-white flex items-center gap-1">
                          <IconBed size={14} className="text-blue-500" />
                          <span>Room {card.room_name || "—"}</span>
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {card.lock_name || "Lock unmapped"}
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <div className="font-semibold text-gray-800 dark:text-gray-200">
                          {card.guest_name || "Walk-in Guest"}
                        </div>
                        {card.guest_phone && (
                          <div className="text-[11px] text-gray-400">{card.guest_phone}</div>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold capitalize text-gray-600 dark:text-gray-300">
                          {card.issuing_method === 'cloud' && <IconWifi size={13} className="text-emerald-500" />}
                          {card.issuing_method === 'encoder' && <IconUsb size={13} className="text-amber-500" />}
                          {card.issuing_method === 'bluetooth' && <IconBluetooth size={13} className="text-blue-500" />}
                          <span>{card.issuing_method}</span>
                        </span>
                      </td>

                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        <div>Until: <b>{card.valid_until ? new Date(card.valid_until).toLocaleString() : "Permanent"}</b></div>
                        <div className="text-[10px] text-gray-400">
                          From: {card.valid_from ? new Date(card.valid_from).toLocaleString() : "—"}
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          card.status === 'active' && !isExpired
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                            : card.status === 'lost'
                            ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                            : card.status === 'cancelled'
                            ? 'bg-gray-100 text-gray-700 dark:bg-neutral-700 dark:text-gray-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                        }`}>
                          {isExpired && card.status === 'active' ? 'Expired' : card.status}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {card.status === 'active' && !isExpired && (
                            <>
                              <button
                                onClick={() => openExtendModal(card)}
                                className="px-2.5 py-1 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-[11px] font-semibold"
                                title="Extend stay period"
                              >
                                Extend
                              </button>
                              <button
                                onClick={() => openLostModal(card)}
                                className="px-2.5 py-1 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 text-[11px] font-semibold"
                                title="Report card lost & issue replacement"
                              >
                                Lost
                              </button>
                              <button
                                onClick={() => handleRevoke(card)}
                                className="px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-[11px] font-semibold"
                                title="Revoke card access"
                              >
                                Revoke
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-gray-200 dark:border-neutral-700 text-xs">
            <span className="text-gray-500">Page {page} of {totalPages} ({total} total cards)</span>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL: ISSUE NEW KEYCARD */}
      <dialog id="modal-issue-card" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-white dark:bg-neutral-800 text-gray-900 dark:text-white max-w-lg">
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-neutral-700">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <IconKey size={18} className="text-emerald-600" />
              <span>Issue RFID Guest Keycard</span>
            </h3>
            <button
              onClick={() => document.getElementById("modal-issue-card").close()}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700"
            >
              <IconX size={16} />
            </button>
          </div>

          <form onSubmit={handleIssueSubmit} className="mt-4 space-y-4 text-xs">
            {/* Room selection */}
            <div>
              <label className="block font-semibold mb-1">Select Hotel Room *</label>
              <select
                value={issueRoomId}
                onChange={(e) => setIssueRoomId(e.target.value)}
                className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                required
              >
                <option value="">-- Choose Room --</option>
                {rooms.map((rm) => (
                  <option key={rm.id} value={rm.id}>
                    Room {rm.name} ({rm.room_type_name || "Standard"})
                  </option>
                ))}
              </select>

              {issueRoomId && (
                <div className="mt-1 text-[11px]">
                  {selectedRoomLock ? (
                    <span className="text-emerald-600 font-semibold flex items-center gap-1">
                      <IconCheck size={13} />
                      Mapped Lock: {selectedRoomLock.lock_name} ({selectedRoomLock.has_gateway ? "Gateway" : "Bluetooth"})
                    </span>
                  ) : (
                    <span className="text-amber-600 font-semibold flex items-center gap-1">
                      <IconAlertTriangle size={13} />
                      Warning: No lock mapped to this room! Key cannot be authorized.
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Guest info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold mb-1">Guest Name</label>
                <input
                  type="text"
                  placeholder="e.g. John Doe"
                  value={issueGuestName}
                  onChange={(e) => setIssueGuestName(e.target.value)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Guest Phone</label>
                <input
                  type="text"
                  placeholder="+977-98..."
                  value={issueGuestPhone}
                  onChange={(e) => setIssueGuestPhone(e.target.value)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                />
              </div>
            </div>

            {/* Card Number / UID */}
            <div>
              <label className="block font-semibold mb-1">
                Card Number / Card UID *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Scan card on USB reader or enter UID"
                  value={issueCardNumber}
                  onChange={(e) => setIssueCardNumber(e.target.value)}
                  className="flex-1 p-2.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 font-mono font-bold"
                  required
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Place the physical RFID card onto your USB desktop reader or type the UID.
              </p>
            </div>

            {/* Issuing Method */}
            <div>
              <label className="block font-semibold mb-1">Issuing Method</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "encoder", name: "USB Encoder", icon: <IconUsb size={16} /> },
                  { id: "cloud", name: "Cloud / Gateway", icon: <IconWifi size={16} /> },
                  { id: "bluetooth", name: "Bluetooth App", icon: <IconBluetooth size={16} /> },
                ].map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setIssueMethod(m.id)}
                    className={`p-2 rounded-xl border text-center flex flex-col items-center gap-1 transition ${
                      issueMethod === m.id
                        ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-bold"
                        : "border-gray-200 dark:border-neutral-700 text-gray-600"
                    }`}
                  >
                    {m.icon}
                    <span className="text-[11px]">{m.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Validity timestamps */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold mb-1">Valid From</label>
                <input
                  type="datetime-local"
                  value={issueValidFrom}
                  onChange={(e) => setIssueValidFrom(e.target.value)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Valid Until (Checkout)</label>
                <input
                  type="datetime-local"
                  value={issueValidUntil}
                  onChange={(e) => setIssueValidUntil(e.target.value)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                  required
                />
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200 dark:border-neutral-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-issue-card").close()}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 dark:hover:bg-neutral-700 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={issueSubmitting || !selectedRoomLock}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1.5 disabled:opacity-50"
              >
                <IconCheck size={16} />
                <span>{issueSubmitting ? "Issuing Card..." : "Confirm & Issue Key"}</span>
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* MODAL: EXTEND STAY */}
      <dialog id="modal-extend-card" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-white dark:bg-neutral-800 text-gray-900 dark:text-white max-w-sm">
          <div className="flex items-center justify-between pb-3 border-b">
            <h3 className="font-bold text-sm">Extend Stay Validity</h3>
            <button
              onClick={() => document.getElementById("modal-extend-card").close()}
              className="p-1 rounded-lg hover:bg-gray-100"
            >
              <IconX size={16} />
            </button>
          </div>

          <form onSubmit={handleExtendSubmit} className="mt-4 space-y-3 text-xs">
            <div className="p-2.5 bg-gray-50 dark:bg-neutral-900/50 rounded-xl border">
              <div>Card: <b>{extendCard?.card_number}</b></div>
              <div>Guest: <b>{extendCard?.guest_name || "Guest"}</b></div>
              <div>Room: <b>{extendCard?.room_name}</b></div>
            </div>

            <div>
              <label className="block font-semibold mb-1">New Valid Until (Checkout)</label>
              <input
                type="datetime-local"
                value={extendValidUntil}
                onChange={(e) => setExtendValidUntil(e.target.value)}
                className="w-full p-2.5 rounded-lg border font-semibold"
                required
              />
            </div>

            <div className="pt-3 flex justify-end gap-2 border-t">
              <button
                type="button"
                onClick={() => document.getElementById("modal-extend-card").close()}
                className="px-3 py-1.5 rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={extendSubmitting}
                className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
              >
                {extendSubmitting ? "Updating..." : "Update Validity"}
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* MODAL: REPLACE LOST CARD */}
      <dialog id="modal-lost-card" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-white dark:bg-neutral-800 text-gray-900 dark:text-white max-w-sm">
          <div className="flex items-center justify-between pb-3 border-b">
            <h3 className="font-bold text-sm flex items-center gap-2 text-amber-600">
              <IconAlertTriangle size={18} />
              <span>Replace Lost Card</span>
            </h3>
            <button
              onClick={() => document.getElementById("modal-lost-card").close()}
              className="p-1 rounded-lg hover:bg-gray-100"
            >
              <IconX size={16} />
            </button>
          </div>

          <form onSubmit={handleLostSubmit} className="mt-4 space-y-3 text-xs">
            <div className="p-2.5 bg-rose-50 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300 rounded-xl border border-rose-200">
              Old card <b>#{lostCard?.card_number}</b> for Room <b>{lostCard?.room_name}</b> will be revoked immediately.
            </div>

            <div>
              <label className="block font-semibold mb-1">New Card Number / UID *</label>
              <input
                type="text"
                placeholder="Scan or enter new card number"
                value={replacementCardNumber}
                onChange={(e) => setReplacementCardNumber(e.target.value)}
                className="w-full p-2.5 rounded-lg border font-mono font-bold"
                required
              />
            </div>

            <div className="pt-3 flex justify-end gap-2 border-t">
              <button
                type="button"
                onClick={() => document.getElementById("modal-lost-card").close()}
                className="px-3 py-1.5 rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={replaceSubmitting}
                className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
              >
                {replaceSubmitting ? "Replacing..." : "Revoke Old & Issue New"}
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </Page>
  );
}
